import React, { useState, useEffect } from "react";
import BillingAutomationHeader from "./BillingAutomationHeader";
import BillingAutomationFilter from "./BillingAutomationFilter";
import filterOpenCloseIcon from "../../assets/svg/filterOpenCloseIcon.svg";
import axiosInstance from "../../axiosInstance";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import CommonPagination from "../../CommonPagination/CommonPagination";
import CommonFooter from "../../CommonFooter/CommonFooter";
import Vector from "../../assets/svg/editIcon.svg";
import Delete from "../../assets/svg/DeleteIcon.svg";
import ConfirmDialog from "../../ConfirmDialog/ConfirmDialog";
import { usePermissions } from '../../hooks/PermissionContext';
import { useModule } from '../../hooks/ModuleContext';
import PermissionWrapper from '../../hooks/PermissionWrapper';
import AdditionalPermissionWrapper from '../../hooks/AdditionalPermissionWrapper';

const BillingAutomationListing = () => {
    const navigate = useNavigate();

    const { activeModuleId } = useModule();
    const { getModulePermission } = usePermissions();
    const permission = getModulePermission(activeModuleId);

    const [showFilter, setShowFilter] = useState(true);
    const [tableData, setTableData] = useState([]);
    const [total, setTotal] = useState(0);

    const [limit, setLimit] = useState(5);
    const [skip, setSkip] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalPages, setTotalPages] = useState(0);

    const [searchTerm, setSearchTerm] = useState("");

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);

    const [filters, setFilters] = useState({
        automationCode: [],
        contractCode: [],
        plantCode: [],
        fromDate: null,
        toDate: null,
        city: [],
        state: [],
        zone: [],
    });

    const fetchBillingAutomation = async (
        term = "",
        appliedFilters = filters
    ) => {
        try {
            const res = await axiosInstance.post(
                "/automation/list",
                {
                    limit,
                    skip,
                    search: term,
                    filter: {
                        automation_code: appliedFilters.automationCode,
                        contract_code: appliedFilters.contractCode,
                        plant_code: appliedFilters.plantCode,
                        from_date: appliedFilters.fromDate
                            ? new Date(appliedFilters.fromDate)
                                .toISOString()
                                .split("T")[0]
                            : "",
                        to_date: appliedFilters.toDate
                            ? new Date(appliedFilters.toDate)
                                .toISOString()
                                .split("T")[0]
                            : "",
                        city: appliedFilters.city,
                        state: appliedFilters.state,
                        zones: appliedFilters.zone
                    }
                }
            );

            setTableData(res.data?.automations || []);
            setTotal(res.data?.totalCount || 0);
            setTotalPages(
                Math.ceil((res.data?.totalCount || 0) / limit)
            );
        } catch (error) {
            toast.error("Failed to fetch billing automation");
        }
    };

    useEffect(() => {
        fetchBillingAutomation(searchTerm);
    }, [skip, limit]);

    const handleSearch = async (term) => {
        setSearchTerm(term);
        setSkip(0);
        setCurrentPage(0);
        await fetchBillingAutomation(term);
    };

    const handleApplyFilters = (data) => {
        setFilters(data);
        setSkip(0);
        setCurrentPage(0);
        fetchBillingAutomation(searchTerm, data);
    };

    const handleResetFilters = () => {
        const empty = {
            automationCode: [],
            contractCode: [],
            plantCode: [],
            fromDate: null,
            toDate: null,
            city: [],
            state: [],
            zone: [],
        };

        setFilters(empty);
        fetchBillingAutomation(searchTerm, empty);
    };

    const handlePageClick = ({ selected }) => {
        setCurrentPage(selected);
        setSkip(selected * limit);
    };

    const handleLimitChange = (e) => {
        setLimit(Number(e.target.value));
        setSkip(0);
        setCurrentPage(0);
    };

    const handleAddNewClick = () => {
        navigate("/billingAutomationCreation");
    };

    const handleEdit = (id) => {
        navigate('/billingAutomationCreation', {
            state: { id, mode: 'edit' }
        });
    };

    const handleView = (id) => {
        if (!permission || permission.view_permission !== "1") {
            return;
        }

        navigate('/billingAutomationViewpage', {
            state: { id }
        });
    };

    const handleDelete = (id) => {
        setSelectedId(id);
        setConfirmOpen(true);
    };

    const confirmDelete = async () => {
        try {
            await axiosInstance.delete(
                `/automation/delete/${selectedId}`
            );
            toast.success("Billing Automation deleted");

            setConfirmOpen(false);
            setSelectedId(null);

            fetchBillingAutomation(searchTerm);

        } catch (error) {
            toast.error("Delete failed", error);
        }
    };

    const cancelDelete = () => {
        setConfirmOpen(false);
        setSelectedId(null);
    };

    return (
        <>
            <BillingAutomationHeader onSearch={handleSearch} />

            <div className="container">
                <div className="header" style={{ marginBottom: "5px" }}>
                    <div className="left-header">
                        <h3>Billing Automation ({total})</h3>
                    </div>

                    <div className="right-header">
                        <PermissionWrapper permissionKey="create_permission">
                            <button
                                id="addNewBtn"
                                onClick={handleAddNewClick}
                            >
                                Add New
                            </button>
                        </PermissionWrapper>

                        <div
                            className={`project-popup-filter-setshow-image ${showFilter ? "active" : ""
                                }`}
                            onClick={() =>
                                setShowFilter(!showFilter)
                            }
                        >
                            <img
                                src={filterOpenCloseIcon}
                                alt="Filter"
                                className="project-popup-filter-icon"
                            />
                        </div>
                    </div>
                </div>

                <div
                    className={`filteropenclose-wrapper ${showFilter ? "open" : ""
                        }`}
                >
                    <BillingAutomationFilter
                        onApply={handleApplyFilters}
                        onReset={handleResetFilters}
                    />
                </div>

                <div style={{ overflowX: "auto" }}>
                    <table>
                        <thead>
                            <tr>
                                <th>S.No</th>
                                <th>Automation Code</th>
                                <th>Contract Code</th>
                                <th>Project</th>
                                <th>No Of Invoice</th>
                                <th>Plant Code</th>
                                <th>Sales Amount</th>
                                <th>Date</th>
                                <th></th>
                            </tr>
                        </thead>

                        <tbody>
                            {tableData.length > 0 ? (
                                tableData.map((row, index) => (
                                    <tr key={row.id}>
                                        <td>{skip + index + 1}</td>
                                        <td onClick={() => handleView(row.id)} style={{ cursor: 'pointer' }}>{row.automation_code}</td>
                                        <td onClick={() => handleView(row.id)} style={{ cursor: 'pointer' }}>{row.contract_code}</td>
                                        <td onClick={() => handleView(row.id)} style={{ cursor: 'pointer' }}>{row.project_name}</td>
                                        <td onClick={() => handleView(row.id)} style={{ cursor: 'pointer' }}>{row.no_of_invoice_creation}</td>
                                        <td onClick={() => handleView(row.id)} style={{ cursor: 'pointer' }}>{row.code}</td>
                                        <td onClick={() => handleView(row.id)} style={{ cursor: 'pointer' }}>{row.sales_amount}</td>
                                        <td onClick={() => handleView(row.id)} style={{ cursor: 'pointer' }}>{row.date}</td>
                                        <td>
                                            <div className="actions" style={{ gap: '10px' }}>
                                                <PermissionWrapper permissionKey="edit_permission">
                                                    <span
                                                        className="edit-icon"
                                                        title="Edit"
                                                        style={{
                                                            cursor: 'pointer',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '30px',
                                                            height: '30px',
                                                            borderRadius: '50%',
                                                            backgroundColor: '#fff',
                                                        }}
                                                        onClick={(e) => { e.stopPropagation(); handleEdit(row.id); }}
                                                    >
                                                        <img src={Vector} alt="Edit" width="14" height="14" />
                                                    </span>
                                                </PermissionWrapper>

                                                <PermissionWrapper permissionKey="delete_permission">
                                                    <span
                                                        className="delete-icon"
                                                        title="Delete"
                                                        style={{
                                                            cursor: 'pointer',
                                                            display: 'inline-flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            width: '30px',
                                                            height: '30px',
                                                            borderRadius: '50%',
                                                            backgroundColor: '#fff',
                                                        }}
                                                        onClick={(e) => { e.stopPropagation(); handleDelete(row.id); }}
                                                    >

                                                        <img src={Delete} alt="Delete" width="27" height="27" />
                                                    </span>
                                                </PermissionWrapper>

                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td
                                        colSpan="9"
                                        style={{
                                            textAlign: "center",
                                            padding: "20px",
                                        }}
                                    >
                                        No Billing Automation Found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                <CommonPagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    limit={limit}
                    onPageChange={handlePageClick}
                    onLimitChange={handleLimitChange}
                />
            </div>
            <ConfirmDialog
                open={confirmOpen}
                title="Delete Confirmation"
                message="Are you sure you want to delete this Billing Automation?"
                confirmText="Delete"
                cancelText="Cancel"
                onConfirm={confirmDelete}
                onCancel={cancelDelete}
            />
            <CommonFooter />
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
        </>
    );
};

export default BillingAutomationListing;